import React, { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { useActiveClinic } from "../../../context/ClinicContext"
import { useClinicSubscription } from "../../subscriptions/hooks/useClinicSubscription"

import usePatientWorkspace from "../hooks/usePatientWorkspace"
import usePatientsViewModel from "../hooks/usePatientsViewModel"
import usePatientFinanceActions from "../hooks/usePatientFinanceActions"
import usePatientsRouteState from "../hooks/usePatientsRouteState"

import PatientFinanceTab from "../components/PatientFinanceTab"
import PatientSummaryTab from "../components/PatientSummaryTab"
import PatientTimelineTab from "../components/PatientTimelineTab"
import PatientFormDrawer from "../components/PatientFormDrawer"
import PatientHeaderCard from "../components/PatientHeaderCard"
import PatientQuickActions from "../components/PatientQuickActions"
import PatientDeleteModal from "../components/PatientDeleteModal"
import PatientMergeModal from "../components/PatientMergeModal"
import { apiFetch } from "../../../utils/apiFetch"
import PatientsListPanel from "../components/PatientsListPanel"
import PlansTab from "../components/PlansTab"
import EmptyPane from "../components/EmptyPane"
import PatientAppointmentsTab from "../components/PatientAppointmentsTab"
import PatientOdontogramTab from "../components/PatientOdontogramTab"

import PatientWorkspaceLayout from "../layouts/PatientWorkspaceLayout"

import { ui } from "../../../shared/styles/uiSystem"
import PillTabs from "../../../shared/components/PillTabs"
import usePatientsListLoader from "../hooks/usePatientsListLoader"
import usePatientFormActions from "../hooks/usePatientFormActions"
import usePatientSelectionSync from "../hooks/usePatientSelectionSync"
import usePatientNavigationActions from "../hooks/usePatientNavigationActions"
import usePatientOdontogramActions from "../hooks/usePatientOdontogramActions"
import StatusBanner from "../../../shared/components/StatusBanner"
import QuickPaymentDrawer from "../components/QuickPaymentDrawer"
import { openAgendaFromPatient } from "../controllers/patientNavigation"

import {
  formatMoney,
  formatDateTime,
  formatDateOnly,
  formatTimeOnly
} from "../utils/patientFormatting"

const tabs = [
  { id: "SUMMARY", label: "Resumen" },
  { id: "APPOINTMENTS", label: "Citas" },
  { id: "ODONTOGRAM", label: "Odontograma" },
  { id: "PLANS", label: "Planes" },
  { id: "FINANCE", label: "Finanzas" },
  { id: "TIMELINE", label: "Actividad" },
  { id: "CLINICAL_HISTORY", label: "Historia" }
]

function PatientsMetricStrip({ metrics = [], isReadOnly = false }) {
  return (
    <div
      style={{
        marginBottom: 10,
        borderRadius: 16,
        border: "1px solid rgba(191,219,254,0.55)",
        background: "linear-gradient(135deg, #0f1f46 0%, #2457d6 100%)",
        boxShadow: "0 10px 24px rgba(37,99,235,0.10)",
        padding: "9px 12px",
        minWidth: 0,
        overflow: "hidden"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          minWidth: 0
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            flexWrap: "wrap",
            minWidth: 0
          }}
        >
          {metrics.map((metric) => (
            <div
              key={metric.label}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                minHeight: 28,
                padding: "0 10px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.16)",
                color: "#ffffff",
                fontSize: 12,
                fontWeight: 850,
                whiteSpace: "nowrap"
              }}
            >
              <span style={{ color: "rgba(255,255,255,0.74)", fontWeight: 800 }}>
                {metric.label}
              </span>
              <span style={{ color: "#ffffff", fontWeight: 950 }}>
                {metric.value}
              </span>
            </div>
          ))}
        </div>

        {isReadOnly ? (
          <div
            style={{
              ...ui.pillDanger,
              minHeight: 28,
              background: "rgba(254,242,242,0.96)"
            }}
          >
            Solo lectura
          </div>
        ) : null}
      </div>
    </div>
  )
}



const PATIENTS_API_BASE =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api"



function getPatientMergeSearchText(patient) {
  return [
    patient?.firstName,
    patient?.lastName,
    patient?.name,
    patient?.phone,
    patient?.email
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
}

export default function PatientsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const { activeClinicId } = useActiveClinic()

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const isMobile = viewportWidth < 720
  const isStacked = viewportWidth < 1180
  const isPatientHeaderStacked = viewportWidth < 1160
  const areQuickActionsStacked = viewportWidth < 980

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const clinicIdFromUrlRaw = queryParams.get("clinicId") || ""
  const tabFromUrl = String(queryParams.get("tab") || "").trim().toUpperCase()

  const activeClinic =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : ""

  const routeClinicId =
    clinicIdFromUrlRaw && clinicIdFromUrlRaw !== "ALL"
      ? clinicIdFromUrlRaw
      : ""

  const effectiveClinicId = id
    ? (routeClinicId || activeClinic)
    : activeClinic

  const [quickPaymentOpen, setQuickPaymentOpen] = useState(false)

  const { subscription } = useClinicSubscription(effectiveClinicId)
  const isReadOnly = subscription?.access?.isReadOnly || false
  const readOnlyReason = subscription?.access?.reason || "La cl\u00ednica est\u00e1 en modo solo lectura."

  const [query, setQuery] = useState("")
  const [activeFilter, setActiveFilter] = useState("ALL")
  const [activeTab, setActiveTab] = useState("SUMMARY")

  const [selectedSurface, setSelectedSurface] = useState("ALL")
  const [newOdontogramCondition, setNewOdontogramCondition] = useState("CARIES")

  const [selectedPatient, setSelectedPatient] = useState(null)
  const [selectedPatientId, setSelectedPatientId] = useState(id || "")
  const [patientDeleteModal, setPatientDeleteModal] = useState({
    open: false,
    mode: "loading",
    preview: null,
    error: "",
    patientName: "",
    resultMessage: "",
    loading: false,
    deleting: false
  })

  const [patientMergeModal, setPatientMergeModal] = useState({
    open: false,
    mode: "select",
    sourcePatientName: "",
    query: "",
    selectedTargetId: "",
    preview: null,
    error: "",
    resultMessage: "",
    loadingPreview: false,
    merging: false
  })
  const [showMobilePatientList, setShowMobilePatientList] = useState(false)

  const paymentsRef = useRef(null)
  const receivablesRef = useRef(null)
  const manualApplyRef = useRef(null)
  const timelineFinanceRef = useRef(null)
  const odontogramSectionRef = useRef(null)
  const lastOdontogramScrollKeyRef = useRef("")

  const [selectedTooth, setSelectedTooth] = useState(null)

  const filters = [
    { id: "ALL", label: "Todos" },
    { id: "DEBT", label: "Con deuda" },
    { id: "PLAN", label: "Con plan" },
    { id: "RECALL", label: "Recall / Promoción" }
  ]

  const resolveSafeClinicId = () => effectiveClinicId || ""
  const safeClinicId = resolveSafeClinicId()
  const isClinicSelectionBlocked = !safeClinicId

  const {
    patients,
    loadingPatients,
    loadedPatientsClinicId,
    loadPatients
  } = usePatientsListLoader({
    resolveSafeClinicId
  })

  const {
    loadingWorkspace,

    patientSummary,
    patientAppointments,
    patientPlans,
    patientOdontogram,

    patientFinance,
    loadingFinance,

    openCredits,
    pendingReceivables,
    loadingAdvancedFinance,

    patientAllocations,
    financialTimeline,
    loadingFinancialTimeline,

    patientTimeline,
    loadingPatientTimeline,

    patientMergeAudits,
    loadingPatientMergeAudits,

    clearWorkspace,
    loadPatientWorkspace,
    loadPatientFinance,
    loadAdvancedFinance,
    loadPatientAllocations,
    loadFinancialTimeline
  } = usePatientWorkspace({
    patientId: selectedPatientId,
    clinicId: safeClinicId
  })

  const {
    patientFormOpen,
    setPatientFormOpen,
    patientFormMode,
    patientForm,
    setPatientForm,
    savingPatientForm,
    openCreatePatientDrawer,
    openEditPatientDrawer,
    submitPatientForm
  } = usePatientFormActions({
    navigate,
    safeClinicId,
    selectedPatientId,
    selectedPatient,
    isReadOnly,
    readOnlyReason,
    query,
    loadPatients
  })

  const {
    openPatient,
    openClinicalHistory
  } = usePatientNavigationActions({
    navigate,
    effectiveClinicId,
    resolveSafeClinicId,
    selectedPatientId,
    patientSummary,
    selectedPatient
  })

  const {
    savingOdontogram,
    saveOdontogramEntry,
    sendOdontogramToPlan
  } = usePatientOdontogramActions({
    isReadOnly,
    readOnlyReason,
    resolveSafeClinicId,
    selectedPatientId,
    selectedTooth,
    selectedSurface,
    newOdontogramCondition,
    loadPatientWorkspace,
    setSelectedSurface,
    setNewOdontogramCondition,
    setActiveTab
  })

  usePatientSelectionSync({
    selectedPatientId,
    safeClinicId,
    patients,
    loadingPatients,
    loadedPatientsClinicId,
    clearWorkspace,
    navigate,
    setSelectedPatient,
    setSelectedPatientId,
    loadPatientWorkspace
  })

  const { focusFromUrl } = usePatientsRouteState({
    location,
    id,
    activeClinicId,
    activeClinic,
    routeClinicId,
    navigate,
    setActiveTab,
    setActiveFilter,
    setSelectedPatientId,
    loadPatientWorkspace,
    safeClinicId
  })

  const patientTabItems = tabs.map((tab) => ({
    value: tab.id,
    label: tab.label,
    onClick: () => {
      if (tab.id === "CLINICAL_HISTORY") {
        openClinicalHistory()
        return
      }

      setActiveTab(tab.id)
    }
  }))

  const {
    filteredPatients,
    moduleMetrics,
    patientName,
    phone,
    email,
    summaryPending,
    recentAppointmentsCount,
    upcomingAppointmentsCount,
    recentTreatmentsCount,
    lastVisit,
    nextVisit,
    patientStatusTag,
    patientInitials
  } = usePatientsViewModel({
    patients,
    activeFilter,
    patientSummary,
    selectedPatient
  })

  const mergeCandidates = useMemo(() => {
    const term = String(patientMergeModal.query || "").trim().toLowerCase()

    return (patients || [])
      .filter((patient) => patient?.id && patient.id !== selectedPatientId)
      .filter((patient) => {
        if (!term) return true

        return getPatientMergeSearchText(patient).includes(term)
      })
      .slice(0, 12)
  }, [patients, selectedPatientId, patientMergeModal.query])

  const {
    applyingPayment,
    reversingAllocation,
    cancellingPayment,
    newPaymentForm,
    setNewPaymentForm,
    manualApplyForm,
    setManualApplyForm,
    savingPayment,
    savingAndPrinting,
    manualReceivableForm,
    setManualReceivableForm,
    savingManualReceivable,
    createManualReceivable,
    applyPaymentManually,
    registerNewPayment,
    reverseAllocation,
    cancelPayment,
    printPaymentReceipt
  } = usePatientFinanceActions({
    selectedPatientId,
    clinicId: safeClinicId,
    isReadOnly,
    readOnlyReason,
    loadPatientFinance,
    loadAdvancedFinance,
    loadPatientAllocations,
    loadFinancialTimeline
  })

  const openQuickPayment = () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura")
      return
    }

    if (!selectedPatientId || !safeClinicId) {
      alert("Selecciona un paciente con cl\u00ednica v\u00e1lida.")
      return
    }

    setQuickPaymentOpen(true)
  }

  const displayPatientTimeline = useMemo(() => {
    const rows = Array.isArray(patientTimeline) ? patientTimeline : []

    const hiddenTypes = new Set([
      "PAYMENT_APPLIED",
      "CREDIT_AUTO_APPLIED"
    ])

    return rows.filter((item) => {
      const type = String(item?.type || "").trim().toUpperCase()
      const title = String(item?.title || "").trim().toLowerCase()
      const subtitle = String(item?.subtitle || "").trim().toLowerCase()

      if (hiddenTypes.has(type)) {
        return false
      }

      const looksLikeAppliedPayment =
        (title.includes("pago") || subtitle.includes("pago")) &&
        (
          title.includes("aplicado") ||
          subtitle.includes("aplicado") ||
          title.includes("automát") ||
          subtitle.includes("automát") ||
          title.includes("aplicación") ||
          subtitle.includes("aplicación")
        )

      if (looksLikeAppliedPayment) {
        return false
      }

      return true
    })
  }, [patientTimeline])

  useEffect(() => {
    const run = async () => {
      await loadPatients(query)
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeClinicId])

  useEffect(() => {
    if (!selectedTooth) {
      setSelectedSurface("ALL")
    }
  }, [selectedTooth])

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadPatients(query)
    }, 250)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  useEffect(() => {
    if (activeTab !== "FINANCE") return
    if (loadingWorkspace || loadingFinance || loadingAdvancedFinance || loadingFinancialTimeline) return
    if (!focusFromUrl) return

    const refMap = {
      PAYMENTS: paymentsRef,
      RECEIVABLES: receivablesRef,
      MANUAL_APPLY: manualApplyRef,
      TIMELINE: timelineFinanceRef
    }

    const targetRef = refMap[focusFromUrl]
    if (targetRef?.current) {
      targetRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }
  }, [
    activeTab,
    focusFromUrl,
    loadingWorkspace,
    loadingFinance,
    loadingAdvancedFinance,
    loadingFinancialTimeline
  ])

  useEffect(() => {
    if (activeTab !== "ODONTOGRAM") return
    if (!selectedPatientId) return
    if (loadingWorkspace) return

    const sectionNode = odontogramSectionRef.current
    if (!sectionNode) return

    const scrollKey = `${selectedPatientId}-${activeTab}-${tabFromUrl}`

    if (lastOdontogramScrollKeyRef.current === scrollKey) {
      return
    }

    lastOdontogramScrollKeyRef.current = scrollKey

    const timer = window.setTimeout(() => {
      sectionNode.scrollIntoView({
        behavior: "smooth",
        block: "start"
      })
    }, 80)

    return () => window.clearTimeout(timer)
  }, [
    activeTab,
    selectedPatientId,
    loadingWorkspace,
    tabFromUrl
  ])

  const workspaceEmptyState =
    isClinicSelectionBlocked ? (
      <EmptyPane text="Selecciona una cl\u00ednica para trabajar pacientes." />
    ) : !selectedPatientId ? (
      <EmptyPane text="Selecciona un paciente para ver su informaci\u00f3n." />
    ) : loadingWorkspace ? (
      <EmptyPane text="Cargando ficha del paciente..." />
    ) : null

  const workspaceHeader = (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        flexWrap: "wrap",
        minWidth: 0
      }}
    >
      <div style={{ flex: "999 1 520px", minWidth: 0 }}>
        <PatientHeaderCard
          patientInitials={patientInitials}
        patientName={patientName}
        patientStatusTag={patientStatusTag}
        phone={phone}
        email={email}
        nextVisit={nextVisit}
        lastVisit={lastVisit}
        summaryPending={summaryPending}
        patientPlans={patientPlans}
        formatMoney={formatMoney}
        formatDateTime={formatDateTime}
          compact={isPatientHeaderStacked}
        />
      </div>

      <div style={{ flex: "1 1 360px", minWidth: 280, alignSelf: "flex-start" }}>
        <PatientQuickActions
          stacked={areQuickActionsStacked}
          isReadOnly={isReadOnly}
          onScheduleAppointment={() => {
            if (!selectedPatientId) return

            const currentClinicId = resolveSafeClinicId()

            if (!currentClinicId) {
              alert("No hay una cl\u00ednica v\u00e1lida seleccionada.")
              return
            }

            openAgendaFromPatient({
              patientId: selectedPatientId,
              clinicId: currentClinicId,
              source: "patients_page"
            })

            navigate("/agenda")
          }}
          onRegisterPayment={openQuickPayment}
          onEditPatient={openEditPatientDrawer}
          onDeletePatient={handleDeletePatient}
        />

        <PatientDeleteModal
          open={patientDeleteModal.open}
          mode={patientDeleteModal.mode}
          patientName={patientDeleteModal.patientName || patientName}
          preview={patientDeleteModal.preview}
          loading={patientDeleteModal.loading}
          deleting={patientDeleteModal.deleting}
          error={patientDeleteModal.error}
          resultMessage={patientDeleteModal.resultMessage}
          onClose={closePatientDeleteModal}
          onConfirmDelete={confirmDeletePatient}
          onMerge={handleMergePatientSoon}
        />

        <PatientMergeModal
          open={patientMergeModal.open}
          mode={patientMergeModal.mode}
          sourcePatientName={patientMergeModal.sourcePatientName || patientName}
          query={patientMergeModal.query}
          candidates={mergeCandidates}
          selectedTargetId={patientMergeModal.selectedTargetId}
          preview={patientMergeModal.preview}
          loadingPreview={patientMergeModal.loadingPreview}
          merging={patientMergeModal.merging}
          error={patientMergeModal.error}
          resultMessage={patientMergeModal.resultMessage}
          onQueryChange={(nextQuery) => {
            setPatientMergeModal((prev) => ({
              ...prev,
              query: nextQuery,
              selectedTargetId: "",
              preview: null,
              error: ""
            }))
          }}
          onSelectTarget={handleSelectMergeTarget}
          onPreview={previewPatientMerge}
          onConfirmMerge={confirmPatientMerge}
          onBack={() => {
            setPatientMergeModal((prev) => ({
              ...prev,
              mode: "select",
              preview: null,
              error: ""
            }))
          }}
          onClose={closePatientMergeModal}
        />

        {isStacked && selectedPatientId ? (
          <button
            type="button"
            onClick={() => setShowMobilePatientList((value) => !value)}
            style={{
              marginTop: 10,
              width: "100%",
              border: "1px solid #bfdbfe",
              background: showMobilePatientList ? "#eff6ff" : "#ffffff",
              color: "#1d4ed8",
              borderRadius: 12,
              minHeight: 40,
              fontWeight: 900,
              cursor: "pointer"
            }}
          >
            {showMobilePatientList ? "Ocultar listado" : "Buscar otro paciente"}
          </button>
        ) : null}
      </div>
    </div>
  )

  const workspaceTabs = (
    <PillTabs
      items={patientTabItems}
      value={activeTab}
      onChange={setActiveTab}
      compact
      wrap
    />
  )


  function closePatientDeleteModal() {
    setPatientDeleteModal({
      open: false,
      mode: "loading",
      preview: null,
      error: "",
      patientName: "",
      resultMessage: "",
      loading: false,
      deleting: false
    })
  }

  async function handleDeletePatient() {
    if (isReadOnly) {
      setPatientDeleteModal({
        open: true,
        mode: "error",
        preview: null,
        error: readOnlyReason || "No tienes permisos para eliminar pacientes.",
        patientName: patientName || "este paciente",
        resultMessage: "",
        loading: false,
        deleting: false
      })
      return
    }

    if (!selectedPatientId) {
      setPatientDeleteModal({
        open: true,
        mode: "error",
        preview: null,
        error: "Selecciona un paciente para eliminar.",
        patientName: "este paciente",
        resultMessage: "",
        loading: false,
        deleting: false
      })
      return
    }

    const fullName = (
      patientName ||
      `${selectedPatient?.firstName || ""} ${selectedPatient?.lastName || ""}`
    ).trim() || "este paciente"

    setPatientDeleteModal({
      open: true,
      mode: "loading",
      preview: null,
      error: "",
      patientName: fullName,
      resultMessage: "",
      loading: true,
      deleting: false
    })

    try {
      const previewResponse = await apiFetch(
        `${PATIENTS_API_BASE}/patients/${selectedPatientId}/delete-preview`
      )

      const preview = await previewResponse.json().catch(() => null)

      if (!previewResponse.ok) {
        throw new Error(
          preview?.message ||
            "No fue posible revisar si el paciente puede eliminarse."
        )
      }

      setPatientDeleteModal({
        open: true,
        mode: preview?.canDelete ? "confirm" : "blocked",
        preview,
        error: "",
        patientName: fullName,
        resultMessage: "",
        loading: false,
        deleting: false
      })
    } catch (error) {
      console.error("Error validando eliminaci\u00f3n de paciente:", error)

      setPatientDeleteModal({
        open: true,
        mode: "error",
        preview: null,
        error: error?.message || "No fue posible revisar si el paciente puede eliminarse.",
        patientName: fullName,
        resultMessage: "",
        loading: false,
        deleting: false
      })
    }
  }

  async function confirmDeletePatient() {
    if (!selectedPatientId) return

    const fullName = patientDeleteModal.patientName || patientName || "este paciente"

    setPatientDeleteModal((prev) => ({
      ...prev,
      deleting: true,
      error: ""
    }))

    try {
      const deleteResponse = await apiFetch(
        `${PATIENTS_API_BASE}/patients/${selectedPatientId}`,
        {
          method: "DELETE"
        }
      )

      const result = await deleteResponse.json().catch(() => null)

      if (!deleteResponse.ok) {
        throw new Error(result?.message || "No fue posible eliminar el paciente.")
      }

      await loadPatients(query)

      setSelectedPatientId("")
      setSelectedPatient(null)

      if (typeof clearWorkspace === "function") {
        clearWorkspace()
      }

      const params = new URLSearchParams()

      if (safeClinicId) {
        params.set("clinicId", String(safeClinicId))
      }

      navigate(
        `/patients${params.toString() ? `?${params.toString()}` : ""}`,
        { replace: true }
      )

      setPatientDeleteModal({
        open: true,
        mode: "success",
        preview: null,
        error: "",
        patientName: fullName,
        resultMessage: result?.message || "Paciente eliminado correctamente.",
        loading: false,
        deleting: false
      })
    } catch (error) {
      console.error("Error eliminando paciente:", error)

      setPatientDeleteModal((prev) => ({
        ...prev,
        mode: "error",
        error: error?.message || "No fue posible eliminar el paciente.",
        loading: false,
        deleting: false
      }))
    }
  }

  function closePatientMergeModal() {
    setPatientMergeModal({
      open: false,
      mode: "select",
      sourcePatientName: "",
      query: "",
      selectedTargetId: "",
      preview: null,
      error: "",
      resultMessage: "",
      loadingPreview: false,
      merging: false
    })
  }

  async function handleMergePatientSoon() {
    if (!selectedPatientId) {
      setPatientDeleteModal((prev) => ({
        ...prev,
        error: "Selecciona un paciente para fusionar."
      }))
      return
    }

    const fullName = (
      patientDeleteModal.patientName ||
      patientName ||
      `${selectedPatient?.firstName || ""} ${selectedPatient?.lastName || ""}`
    ).trim() || "este paciente"

    setPatientDeleteModal((prev) => ({
      ...prev,
      open: false
    }))

    setPatientMergeModal({
      open: true,
      mode: "select",
      sourcePatientName: fullName,
      query: "",
      selectedTargetId: "",
      preview: null,
      error: "",
      resultMessage: "",
      loadingPreview: false,
      merging: false
    })

    try {
      await loadPatients("")
    } catch (error) {
      console.warn("No fue posible refrescar pacientes antes de fusionar:", error)
    }
  }

  function handleSelectMergeTarget(targetPatientId) {
    setPatientMergeModal((prev) => ({
      ...prev,
      selectedTargetId: targetPatientId,
      preview: null,
      error: ""
    }))
  }

  async function previewPatientMerge() {
    if (!selectedPatientId) return

    if (!patientMergeModal.selectedTargetId) {
      setPatientMergeModal((prev) => ({
        ...prev,
        error: "Selecciona el paciente destino."
      }))
      return
    }

    setPatientMergeModal((prev) => ({
      ...prev,
      loadingPreview: true,
      error: "",
      preview: null
    }))

    try {
      const response = await apiFetch(
        `${PATIENTS_API_BASE}/patients/${selectedPatientId}/merge-preview?targetPatientId=${encodeURIComponent(patientMergeModal.selectedTargetId)}`
      )

      const result = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(result?.message || "No fue posible revisar la fusi\u00f3n.")
      }

      setPatientMergeModal((prev) => ({
        ...prev,
        mode: "preview",
        preview: result,
        error: "",
        loadingPreview: false
      }))
    } catch (error) {
      console.error("Error revisando fusi\u00f3n de pacientes:", error)

      setPatientMergeModal((prev) => ({
        ...prev,
        error: error?.message || "No fue posible revisar la fusi\u00f3n.",
        loadingPreview: false
      }))
    }
  }

  async function confirmPatientMerge() {
    if (!selectedPatientId || !patientMergeModal.selectedTargetId) return

    setPatientMergeModal((prev) => ({
      ...prev,
      merging: true,
      error: ""
    }))

    try {
      const response = await apiFetch(
        `${PATIENTS_API_BASE}/patients/${selectedPatientId}/merge`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            targetPatientId: patientMergeModal.selectedTargetId
          })
        }
      )

      const result = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(result?.message || "No fue posible fusionar los pacientes.")
      }

      const targetPatientId =
        result?.targetPatient?.id || patientMergeModal.selectedTargetId

      await loadPatients(query)

      setSelectedPatientId(targetPatientId)

      if (result?.targetPatient) {
        setSelectedPatient(result.targetPatient)
      }

      if (typeof loadPatientWorkspace === "function") {
        await loadPatientWorkspace(targetPatientId)
      }

      const params = new URLSearchParams()

      if (safeClinicId) {
        params.set("clinicId", String(safeClinicId))
      }

      navigate(
        `/patients/${targetPatientId}${params.toString() ? `?${params.toString()}` : ""}`,
        { replace: true }
      )

      setPatientMergeModal((prev) => ({
        ...prev,
        mode: "success",
        preview: null,
        error: "",
        resultMessage: result?.message || "Pacientes fusionados correctamente.",
        merging: false
      }))
    } catch (error) {
      console.error("Error fusionando pacientes:", error)

      setPatientMergeModal((prev) => ({
        ...prev,
        error: error?.message || "No fue posible fusionar los pacientes.",
        merging: false
      }))
    }
  }



const shouldCollapsePatientListOnMobile = isStacked && Boolean(selectedPatientId) && !showMobilePatientList

  const patientListPanel = shouldCollapsePatientListOnMobile ? null : (
    <PatientsListPanel
      filteredPatients={filteredPatients}
      selectedPatientId={selectedPatientId}
      loadingPatients={loadingPatients}
      isClinicSelectionBlocked={isClinicSelectionBlocked}
      isReadOnly={isReadOnly}
      query={query}
      setQuery={setQuery}
      activeFilter={activeFilter}
      setActiveFilter={setActiveFilter}
      filters={filters}
      focusFromUrl={focusFromUrl}
      onCreatePatient={openCreatePatientDrawer}
      onOpenPatient={(patientId) => {
        setShowMobilePatientList(false)
        openPatient(patientId)
      }}
      compact={isStacked}
    />
  )

  return (
    <div style={ui.page}>
      <PatientFormDrawer
        open={patientFormOpen}
        mode={patientFormMode}
        form={patientForm}
        setForm={setPatientForm}
        onClose={() => setPatientFormOpen(false)}
        onSubmit={submitPatientForm}
        saving={savingPatientForm}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
      />

      <QuickPaymentDrawer
        open={quickPaymentOpen}
        onClose={() => setQuickPaymentOpen(false)}
        patientName={patientName}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
        formatMoney={formatMoney}
        newPaymentForm={newPaymentForm}
        setNewPaymentForm={setNewPaymentForm}
        pendingReceivables={pendingReceivables}
        registerNewPayment={registerNewPayment}
        savingPayment={savingPayment}
        savingAndPrinting={savingAndPrinting}
      />

      {!isMobile ? (
        <PatientsMetricStrip
          metrics={[
            { label: "Total", value: moduleMetrics.total },
            { label: "Con deuda", value: moduleMetrics.withDebt },
            { label: "Con plan", value: moduleMetrics.withPlans },
            { label: "Recall", value: moduleMetrics.recallPending }
          ]}
          isReadOnly={isReadOnly}
        />
      ) : null}

      {isReadOnly && (
        <StatusBanner
          tone="danger"
          title="Modo solo lectura"
          text={readOnlyReason}
          style={{ marginBottom: 16 }}
        />
      )}

      <PatientWorkspaceLayout
        isStacked={isStacked}
        leftPanel={patientListPanel}
        emptyState={workspaceEmptyState}
        header={workspaceHeader}
        tabs={workspaceTabs}
      >
        {activeTab === "SUMMARY" && (
          <PatientSummaryTab
            formatMoney={formatMoney}
            formatDateTime={formatDateTime}
            patientSummary={patientSummary}
            lastVisit={lastVisit}
            nextVisit={nextVisit}
            summaryPending={summaryPending}
            recentAppointmentsCount={recentAppointmentsCount}
            upcomingAppointmentsCount={upcomingAppointmentsCount}
            recentTreatmentsCount={recentTreatmentsCount}
          />
        )}

        {activeTab === "TIMELINE" && (
          <PatientTimelineTab
            loadingPatientTimeline={loadingPatientTimeline}
            patientTimeline={displayPatientTimeline}
            patientMergeAudits={patientMergeAudits}
            loadingPatientMergeAudits={loadingPatientMergeAudits}
            formatDateTime={formatDateTime}
            formatMoney={formatMoney}
          />
        )}

        {activeTab === "APPOINTMENTS" && (
          <PatientAppointmentsTab
            patientAppointments={patientAppointments}
            formatDateOnly={formatDateOnly}
            formatTimeOnly={formatTimeOnly}
            navigate={navigate}
            resolveSafeClinicId={resolveSafeClinicId}
          />
        )}

        {activeTab === "PLANS" && (
          <PlansTab
            patientPlans={patientPlans}
            patientAppointments={patientAppointments}
            selectedPatientId={selectedPatientId}
            clinicId={resolveSafeClinicId()}
            patientName={patientName}
            formatMoney={formatMoney}
            navigate={navigate}
            isReadOnly={isReadOnly}
            readOnlyReason={readOnlyReason}
            onReloadPlans={async () => {
              if (selectedPatientId) {
                await loadPatientWorkspace(selectedPatientId)
              }
            }}
          />
        )}

        {activeTab === "ODONTOGRAM" && (
          <div ref={odontogramSectionRef} id="patient-odontogram-section">
            <PatientOdontogramTab
              isReadOnly={isReadOnly}
              readOnlyReason={readOnlyReason}
              patientOdontogram={patientOdontogram}
              selectedTooth={selectedTooth}
              setSelectedTooth={setSelectedTooth}
              selectedSurface={selectedSurface}
              setSelectedSurface={setSelectedSurface}
              newOdontogramCondition={newOdontogramCondition}
              setNewOdontogramCondition={setNewOdontogramCondition}
              saveOdontogramEntry={saveOdontogramEntry}
              sendOdontogramToPlan={sendOdontogramToPlan}
              savingOdontogram={savingOdontogram}
              formatDateTime={formatDateTime}
            />
          </div>
        )}

        {activeTab === "FINANCE" && (
          <PatientFinanceTab
            isReadOnly={isReadOnly}
            readOnlyReason={readOnlyReason}
            formatMoney={formatMoney}
            formatDateTime={formatDateTime}

            patientFinance={patientFinance}
            loadingFinance={loadingFinance}

            openCredits={openCredits}
            pendingReceivables={pendingReceivables}
            loadingAdvancedFinance={loadingAdvancedFinance}

            patientAllocations={patientAllocations}
            reversingAllocation={reversingAllocation}
            reverseAllocation={reverseAllocation}

            cancellingPayment={cancellingPayment}
            cancelPayment={cancelPayment}
            printPaymentReceipt={printPaymentReceipt}

            financialTimeline={financialTimeline}
            loadingFinancialTimeline={loadingFinancialTimeline}

            paymentsRef={paymentsRef}
            receivablesRef={receivablesRef}
            manualApplyRef={manualApplyRef}
            timelineFinanceRef={timelineFinanceRef}

            newPaymentForm={newPaymentForm}
            setNewPaymentForm={setNewPaymentForm}
            registerNewPayment={registerNewPayment}
            savingPayment={savingPayment}
            savingAndPrinting={savingAndPrinting}

            manualReceivableForm={manualReceivableForm}
            setManualReceivableForm={setManualReceivableForm}
            createManualReceivable={createManualReceivable}
            savingManualReceivable={savingManualReceivable}

            manualApplyForm={manualApplyForm}
            setManualApplyForm={setManualApplyForm}
            applyPaymentManually={applyPaymentManually}
            applyingPayment={applyingPayment}
          />
        )}
      </PatientWorkspaceLayout>
    </div>
  )
}

